import { useEffect, useMemo } from 'react';

import {
  SelectOption,
  useFormContext,
  useWatch,
} from '@kanda-libs/ks-component-ts';

import { useCurrentJob, useCurrentJobLead } from 'hooks';
import { FIELD_NAMES } from 'pages/Quote/constants';
import { getDepositLimit } from 'pages/Quote/helpers';

import { formatDuration } from './helpers';
import useOutOfBoundsDeposit from './useOutOfBoundsDeposit';

import type { CheckoutFinanceRate } from '../constants';

interface TermsHook {
  options: SelectOption[];
  showBNPLNotice: boolean;
  selectedRate?: CheckoutFinanceRate;
}

export default function useTerms(
  rates: CheckoutFinanceRate[] = [],
  bnpl = false,
): TermsHook {
  const { job } = useCurrentJob();
  const { lead } = useCurrentJobLead();
  const [duration] = useWatch({
    name: [FIELD_NAMES.duration],
  });
  const { setValue } = useFormContext();

  useOutOfBoundsDeposit(rates);

  const [selectedRate] =
    rates.filter((rate: CheckoutFinanceRate) => rate.duration === duration) ||
    [];

  const depositLimits = useMemo(
    () => getDepositLimit(selectedRate?.name, job, lead),
    [selectedRate?.name, job, lead],
  );

  const options = useMemo(
    () =>
      rates
        .sort((rate1: CheckoutFinanceRate, rate2: CheckoutFinanceRate) => {
          if (bnpl)
            return (
              Number(rate1.duration) +
              Number(rate1.deferredDuration) -
              (Number(rate2.duration) + Number(rate2.deferredDuration))
            );
          if (rate1.interest === rate2.interest)
            return Number(rate1.duration) - Number(rate2.duration);
          return Number(rate1.interest) - Number(rate2.interest);
        })
        .filter((rate) => {
          // Filter out invalid rates
          const rateDepositLimits = getDepositLimit(rate.name, job, lead);

          return rateDepositLimits.isValid;
        })
        .map((rate: CheckoutFinanceRate) => ({
          name: formatDuration(rate),
          value: String(rate.duration),
          className: 'min-w-24 ml-1 mt-2',
        })),
    [rates, job, lead, bnpl],
  );

  // Effect sets provider on rate change
  useEffect(() => {
    const newRate = rates.filter(
      (rate: CheckoutFinanceRate) => rate.duration === duration,
    )?.[0];
    const provider = newRate?.provider;
    const deferredDuration = newRate?.deferredDuration;
    if (!provider) return;
    setValue('provider', provider);
    setValue('deferredDuration', deferredDuration);
    if (!bnpl) {
      setValue('bnplInterest', undefined);
      return;
    }
    setValue('bnplInterest', `${parseInt(newRate?.interest, 10) / 100}`);
  }, [duration, rates, setValue, bnpl]);

  // Effect ensures min deposit is met on rate change
  useEffect(() => {
    setValue('depositAmount', depositLimits.min);
  }, [duration, depositLimits.min, setValue]);

  return {
    options,
    showBNPLNotice: bnpl,
    selectedRate,
  };
}
